import React from 'react';
import useBrandIcon from "../../../hooks/useBrandIcon";
import ContributionItems from "../../molecules/cards/ContributionItems";
import useVocalParts from "../../../hooks/useVocalParts";
import {Link, useNavigate} from "react-router-dom";
import {CommunityEntry} from "../../../utils/types";
import API from "../../../helpers/network/http";
import ButtonIcon from "../ButtonIcon";
import {FaThumbsUp} from "react-icons/fa";
import HoverTooltip from "../HoverTooltip";
import Avatar from "../../../pages/community/components/atoms/Avatar";
import {useI18n} from "../../../i18n";

interface Props {
    // TODO: Review if undefined causes and bug on UI - fallback to a default value for community entry
    community_entry: CommunityEntry | undefined;
    isRequest?: boolean;
    showUpvotes?: boolean;
    show_all?: boolean;
    default_part?: string;
    showWaveFormDefault?: boolean;
}

const CommunityCard = ({
                           community_entry,
                           isRequest,
                           showUpvotes,
                           show_all,
                           default_part = 'Tenor',
                           showWaveFormDefault
                       }: Props) => {
    const {t} = useI18n();

    const navigation = useNavigate();
    const fullscreen = useBrandIcon().fullscreen;
    const filter = useBrandIcon().filter;
    const notes = useBrandIcon().notes;
    const star_music_plain = useBrandIcon().star_music_plain;
    const music_strikethrough = useBrandIcon().note_strikethrough;
    const [part, setPart] = React.useState(default_part);
    const [localUpvotes, setLocalUpvotes] = React.useState(community_entry?.upvotes || 0);
    const [hasUpvoted, setHasUpvoted] = React.useState(false);


    const {showContributions, contributions} = useVocalParts({
        part: part,
        song_title: 'Praise your name'
    });


    const handleUpvote = async () => {
        if (hasUpvoted) return;
        try {
            setLocalUpvotes(prev => prev + 1);
            setHasUpvoted(true);
            await API.post(`/community-contributions/upvote/${community_entry?.request_id}`);
        } catch (e) {
            console.error(t('components.communityCard.upvoteFailed'), e);
        }
    };


    return (
        <div className="community_card w-full rounded-[15px] py-0 mb-6">
            <div className="community_card_header mb-8 pt-6 px-6">
                {/* Song Metadata Section */}
                <div className="flex flex-col gap-2 mb-4">
                    <div className="flex justify-between items-center mb-0">
                        <p className="text-white text-sm opacity-80">{t('components.communityCard.songTitle')}</p>
                        {showContributions && !show_all && <div className="full_screen_group">
                            <div
                                className="flex justify-center gap-2 items-center text-white text-opacity-80 text-base font-normal cursor-pointer"
                                onClick={() => navigation(`/community/contributions/${community_entry!.song_uuid}`)}
                            >
                                <small>{t('components.communityCard.expandToFullView')}</small>
                                <img src={fullscreen} alt={t('components.communityCard.fullscreenAlt')}/>
                            </div>

                        </div>}
                    </div>
                    <h2 className="text-white text-xl font-semibold">{community_entry?.metadata?.title || community_entry?.title}</h2>
                    <p className="text-white text-sm opacity-70">{community_entry?.metadata?.artist}</p>
                </div>
                <img
                    src={community_entry?.metadata?.song_art || community_entry?.song_art}
                    alt={t('components.communityCard.songArtAlt')}
                    className="w-full h-48 object-cover rounded-xl mb-4"
                />
                {showUpvotes && (
                    <div className="flex items-center justify-between mt-2 mb-2">
                        <div className="flex items-center gap-0 text-white text-sm opacity-80">
                            <ButtonIcon icon={<FaThumbsUp
                                className={community_entry?.has_upvoted || hasUpvoted ? 'text-ORANGE' : 'text-white'}
                            />} onClick={() => {
                                handleUpvote().then(r => r)
                            }}
                                        size={'small'} className={'!bg-transparent m-0'}/>

                            <span>{localUpvotes || ''} </span>
                        </div>

                        {isRequest && (
                            <div className="flex items-center gap-2 text-white text-sm opacity-60">
                                <img src={music_strikethrough} alt={t('components.communityCard.missingPartAlt')} className="w-4 h-4"/>
                                <span>{t('components.communityCard.partNeeded')}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Top Contributors */}
                {community_entry?.top_contributors && community_entry.top_contributors.length > 0 && (
                    <div className="mt-4">
                        <h4 className="text-white font-semibold mb-2">{t('components.communityCard.topContributors')}</h4>
                        <div className="flex items-center -space-x-3">
                            {community_entry.top_contributors.map((contributor, index) => (
                                <HoverTooltip
                                    key={contributor.email + index}
                                    content={
                                        `${(contributor as any)?.name || (contributor as any)?.full_name || contributor.username || t('components.communityCard.contributorFallback')}${contributor.username ? ` (@${contributor.username})` : ''}`
                                    }
                                >
                                    <div
                                        className="relative inline-flex rounded-full border-2 border-[#0f1014] bg-neutral-800"
                                        style={{zIndex: Math.max(1, 20 - index)}}
                                    >
                                        <Avatar
                                            src={contributor.avatar}
                                            alt={contributor.username || t('components.communityCard.contributorAvatarAlt')}
                                            size={38}
                                            className="shrink-0"
                                        />
                                    </div>
                                </HoverTooltip>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {(showContributions || show_all) && <div className={'contribution_list'}>
                <ContributionItems contributions={contributions}/>

            </div>}

            {!show_all && <div className={`community_card_bottom_body bg-neutral-950 pt-4 pb-6 px-6`}>
                <Link
                    to={`/community/contributions/${community_entry!.group_id}`}
                    className="see_more small_text text-ORANGE">{t('components.communityCard.seeAllContributions')}
                </Link>
            </div>}
        </div>
    );
};

export default CommunityCard;
